import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cacheCheckListData, CachePayload } from 'src/pages/Hindsighting/StyleColorReview/StyleColorReview.slice';
import { ViewDataState } from 'src/types/Domain';
import { Pivot, ListDataOptions } from 'src/worker/pivotWorker.types';
import { IdentityPropsConfig } from 'src/components/StandardCardView/StandardCardView.types';
import { WorklistViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { GroupViewItem } from 'configuration-schemas/viewdefns/general';
import type { ThunkApi } from 'src/store.types';
import { isNil } from 'lodash';

export interface WorklistSlice {
  viewDefn: WorklistViewDefn | undefined;
  isConfigLoading: boolean;
  isDataLoading: boolean;
  liveData: Pivot;
  cachedData: Pivot;
  viewDataState: ViewDataState;
  worklistFlowStatus: number[];
  worklistSearch: string;
  selectedItemId?: string;
  selectedLevel?: GroupViewItem;
  identityPropsConfig?: IdentityPropsConfig;
  cacheHash: string | null;
}

const initialState: WorklistSlice = {
  viewDefn: undefined,
  isConfigLoading: false,
  isDataLoading: false,
  viewDataState: ViewDataState.idle,
  worklistFlowStatus: [],
  worklistSearch: '',
  cachedData: {
    tree: [],
    flat: [],
  },
  liveData: {
    tree: [],
    flat: [],
  },
  cacheHash: null,
};
const worklistSliceReducer = createSlice({
  name: 'Worklist',
  initialState: initialState,
  reducers: {
    requestWorklistConfig(state) {
      state.isConfigLoading = true;
      state.viewDefn = undefined;
    },
    receiveWorklistConfig(state, action: PayloadAction<WorklistViewDefn>) {
      state.isConfigLoading = false;
      state.viewDefn = action.payload;
    },
    requestData(state) {
      state.viewDataState = ViewDataState.liveDataLoadingNoCache;
      state.cachedData = initialState.cachedData;
      state.liveData = initialState.liveData;
    },
    receiveCacheHash(state, action: PayloadAction<string>) {
      state.cacheHash = action.payload;
    },
    receiveCachedData(state, action: PayloadAction<CachePayload>) {
      const { cacheHash, data } = action.payload;
      state.viewDataState = ViewDataState.liveDataLoadingFoundCache;
      if (cacheHash === state.cacheHash) {
        state.cachedData = data;
      }
    },
    receiveLiveData(state, action: PayloadAction<CachePayload>) {
      const { cacheHash, data } = action.payload;
      state.viewDataState = ViewDataState.liveDataReady;
      if (cacheHash === state.cacheHash) {
        state.liveData = data;
      }
    },
    updateSelectedItem(state, action: PayloadAction<string | undefined>) {
      state.selectedItemId = action.payload;
    },
    updateSelectedLevel(state, action: PayloadAction<GroupViewItem | undefined>) {
      state.selectedLevel = action.payload;
    },
    updateWorklistFlowStatus(state, action: PayloadAction<number[]>) {
      state.worklistFlowStatus = action.payload;
    },
    updateWorklistSearch(state, action: PayloadAction<string>) {
      state.worklistSearch = action.payload;
    },
    receiveWorklistError(_state, _action) {
      return initialState;
    },
    cleanUp() {
      return initialState;
    },
  },
});

export const {
  requestWorklistConfig,
  receiveWorklistConfig,
  receiveCacheHash,
  requestData,
  receiveCachedData,
  receiveLiveData,
  updateSelectedItem,
  updateSelectedLevel,
  updateWorklistFlowStatus,
  updateWorklistSearch,
  receiveWorklistError,
  cleanUp,
} = worklistSliceReducer.actions;
export default worklistSliceReducer.reducer;

export function fetchCompanionListData(modelDefn: string, options: ListDataOptions) {
  return cacheCheckListData(modelDefn, requestData, receiveCacheHash, receiveCachedData, receiveLiveData, options);
}

export const maybeToggleSelection = createAsyncThunk<void, string, ThunkApi & { rejectValue: string }>(
  'Worklist/toggleSelection',
  async (id, { dispatch, getState }) => {
    const currentId = getState().worklist.selectedItemId;

    if (!isNil(currentId) && currentId === id) {
      dispatch(updateSelectedItem(undefined));
    }
  },
);
